<template>
  <v-dialog v-model="internalOpen" max-width="700px" persistent>
    <v-card class="border-radius-xl">
      <v-card-title class="pa-6 pb-3">
        <div class="d-flex align-center">
          <i
            class="material-icons-round text-warning me-3"
            style="font-size: 32px"
            >warning</i
          >
          <div>
            <h5 class="font-weight-bold text-h5 text-typo mb-0">
              Merge-Konflikt erkannt
            </h5>
            <p class="text-sm text-body mb-0" v-if="lastEditByName">
              Zuletzt bearbeitet von
              <strong>{{ lastEditByName }}</strong>
            </p>
          </div>
        </div>
      </v-card-title>

      <v-card-text class="px-6 pb-3">
        <v-alert type="warning" dense outlined class="mb-4 border-radius-lg">
          Das Event wurde zwischenzeitlich von jemand anderem bearbeitet. Deine
          Änderungen könnten Daten überschreiben.
        </v-alert>

        <!-- Differenz-Tabelle -->
        <v-simple-table
          v-if="differences.length > 0"
          dense
          class="border-radius-lg"
        >
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Feld
                </th>
                <th class="text-left text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Meine Version
                </th>
                <th class="text-left text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Server-Version
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="diff in differences" :key="diff.field">
                <td class="text-sm font-weight-bold text-typo">
                  {{ diff.label }}
                </td>
                <td>
                  <v-chip
                    v-if="diff.type === 'boolean'"
                    :color="diff.localValue ? 'success' : 'default'"
                    small
                    label
                    class="font-weight-bold"
                  >
                    {{ diff.localValue ? "Ja" : "Nein" }}
                  </v-chip>
                  <span v-else class="text-sm">{{
                    formatValue(diff.localValue)
                  }}</span>
                </td>
                <td>
                  <v-chip
                    v-if="diff.type === 'boolean'"
                    :color="diff.serverValue ? 'success' : 'default'"
                    small
                    label
                    class="font-weight-bold"
                  >
                    {{ diff.serverValue ? "Ja" : "Nein" }}
                  </v-chip>
                  <span v-else class="text-sm">{{
                    formatValue(diff.serverValue)
                  }}</span>
                  <i
                    v-if="diff.changed"
                    class="material-icons-round text-warning ms-1"
                    style="font-size: 16px"
                    >warning</i
                  >
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <p v-else class="text-sm text-body mt-2">
          Keine Feldunterschiede erkennbar (möglicherweise nur Metadaten-Änderung).
        </p>
      </v-card-text>

      <v-card-actions class="pa-6 pt-3">
        <v-btn
          elevation="0"
          :ripple="false"
          height="43"
          class="
            font-weight-bold
            text-uppercase
            btn-default
            bg-gradient-secondary
            py-2
            px-6
            me-2
          "
          color="#5e72e4"
          small
          @click="cancel"
        >
          <i class="material-icons-round me-1" style="font-size: 16px">close</i>
          Abbrechen
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          elevation="0"
          :ripple="false"
          height="43"
          class="
            font-weight-bold
            text-uppercase
            btn-default
            bg-gradient-info
            py-2
            px-6
            me-2
          "
          color="#5e72e4"
          small
          @click="useServerVersion"
        >
          <i class="material-icons-round me-1" style="font-size: 16px">cloud_download</i>
          Server-Version
        </v-btn>
        <v-btn
          elevation="0"
          :ripple="false"
          height="43"
          class="
            font-weight-bold
            text-uppercase
            btn-default
            bg-gradient-warning
            py-2
            px-6
          "
          color="#5e72e4"
          small
          @click="forceMyVersion"
        >
          <i class="material-icons-round me-1" style="font-size: 16px">publish</i>
          Meine Version erzwingen
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "EventConflictDialog",
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    conflictData: {
      type: Object,
      default: () => null,
    },
    localData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      internalOpen: false,
    };
  },
  watch: {
    open(val) {
      this.internalOpen = val;
    },
    internalOpen(val) {
      if (!val) {
        this.$emit("close");
      }
    },
  },
  computed: {
    serverVersion() {
      return this.conflictData && this.conflictData.serverVersion
        ? this.conflictData.serverVersion
        : {};
    },
    lastEditByName() {
      const editor =
        this.conflictData && this.conflictData.lastEditBy
          ? this.conflictData.lastEditBy
          : this.serverVersion.lastEditBy;
      if (!editor) return null;
      const parts = [editor.firstname, editor.lastname].filter(Boolean);
      return parts.length > 0 ? parts.join(" ") : editor.email || "Unbekannt";
    },
    differences() {
      if (!this.localData || !this.serverVersion) return [];

      const fieldMap = {
        // Content
        title: { label: "Titel", type: "string" },
        description: { label: "Beschreibung", type: "string" },
        disclaimer: { label: "Disclaimer", type: "string" },
        organizer: { label: "Veranstalter", type: "string" },
        hints: { label: "Hinweise", type: "string" },
        html: { label: "HTML-Inhalt", type: "string" },
        short: { label: "Kurztext", type: "string" },
        // Status
        active: { label: "Event aktiv", type: "boolean" },
        entries_active: { label: "Anmeldung aktiv", type: "boolean" },
        approved: { label: "Genehmigt", type: "boolean" },
        archived: { label: "Archiviert", type: "boolean" },
        // Dates
        date: { label: "Datum", type: "date" },
        date_start: { label: "Startdatum", type: "date" },
        date_end: { label: "Enddatum", type: "date" },
        date_inlet: { label: "Einlass", type: "date" },
        // Location
        venue: { label: "Veranstaltungsort", type: "string" },
        street: { label: "Straße", type: "string" },
        city: { label: "Stadt", type: "string" },
        zipcode: { label: "PLZ", type: "string" },
        website: { label: "Website", type: "string" },
        // Pricing
        price: { label: "Preis", type: "number" },
        price_lowered: { label: "Ermäßigter Preis", type: "number" },
        price_lowered_info: { label: "Ermäßigung Info", type: "string" },
        price_local: { label: "Ortspreis", type: "number" },
        vat_value: { label: "MwSt. (%)", type: "number" },
        // Capacity & Config
        slots: { label: "Plätze", type: "number" },
        seat_type: { label: "Sitzplatztyp", type: "string" },
        invoicePrefix: { label: "Rechnungspräfix", type: "string" },
        // Flags
        hide_ticket_amount: { label: "Ticketanzahl verstecken", type: "boolean" },
        personalization_required: { label: "Personalisierung erforderlich", type: "boolean" },
        address_required: { label: "Adresse erforderlich", type: "boolean" },
      };

      const diffs = [];
      for (const [field, meta] of Object.entries(fieldMap)) {
        const localVal = this.localData[field];
        const serverVal = this.serverVersion[field];

        // Only compare fields that were part of the local payload
        if (field in this.localData) {
          const changed = this.valuesAreDifferent(localVal, serverVal, meta.type);
          if (changed) {
            diffs.push({
              field,
              label: meta.label,
              type: meta.type,
              localValue: localVal,
              serverValue: serverVal,
              changed,
            });
          }
        }
      }
      return diffs;
    },
  },
  methods: {
    valuesAreDifferent(a, b, type) {
      if (type === "date") {
        return new Date(a).getTime() !== new Date(b).getTime();
      }
      if (type === "number") {
        return Number(a) !== Number(b);
      }
      // eslint-disable-next-line eqeqeq
      return a != b;
    },
    formatValue(val) {
      if (val === undefined || val === null) return "–";
      if (val instanceof Date || (typeof val === "string" && !isNaN(Date.parse(val)) && val.includes("T"))) {
        return new Date(val).toLocaleString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      if (typeof val === "number") {
        return val.toLocaleString("de-DE");
      }
      const str = String(val);
      return str.length > 80 ? str.substring(0, 80) + "…" : str;
    },
    cancel() {
      this.internalOpen = false;
      this.$emit("close");
    },
    useServerVersion() {
      this.internalOpen = false;
      this.$emit("use-server-version", this.serverVersion);
    },
    forceMyVersion() {
      this.internalOpen = false;
      this.$emit("force-my-version");
    },
  },
};
</script>
